// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.3.1"
//
// Changelog:
//
// Version 1.3.1:
// - [Changed] The 'comment_added' SSE handler now emits an event on the global
//   eventEmitter, allowing components like open modals to update in real-time.
//
// Version 1.3.0:
// - [Added] SSE handler for 'comment_added' notifications to provide real-time updates on planner items.
//
// Version 1.2.0:
// - [Changed] BREAKING: Reworked the `new_message` SSE handler to process the full message object directly, enabling real-time UI updates without re-fetching.
// - [Changed] The `registerNewMessageCallback` now expects a callback that accepts the full `Message` object instead of just IDs.
//
// Version 1.1.0:
// - [Added] SSE handler for `resource_shared_with_group` notifications.
// - [Changed] SSE handler for `new_message` to correctly identify group messages.
//
// Version 1.0.0:
// - Initial version of the AuthContext.
// ==============================================================================
import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef, useMemo } from 'react';
import { User, AppointmentStatusEnum, CoachSubscription, ShowcaseProgressEventData, Message, Comment } from '../types';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, API_BASE_URL } from '../config';
import authService, { LoginCredentials } from '../services/authService';
import paymentService from '../services/paymentService';
import communicationService from '../services/communicationService';
import aiCreditsService from '../services/aiCreditsService';
import { toast } from 'react-hot-toast';
import { UserRoleEnum, NotificationTypeEnum } from '../types/enums';
import eventEmitter from '../utils/eventEmitter';

export interface NewMessageEventPayload {
  sender_user_id: number;
  receiver_user_id: number;
  related_entity_id?: number;
  group_conversation_id?: number; // NEW
}

export interface MessageDeletedEventPayload {
  message_id: number;
}

export interface MessageReadEventPayload {
  message_id: number;
  read_at: string;
  reader_user_id: number;
}

export interface AppointmentStatusChangedEventPayload {
  appointment_id: number;
  new_status: AppointmentStatusEnum;
  changer_user_id?: number; 
}

export interface IntakeFormEventPayload {
    client_user_id: number;
    coach_user_id: number;
    intake_form_id: number;
    status: 'pending' | 'submitted' | 'reviewed';
}

export interface InvoiceStatusUpdatedEventPayload {
  invoice_id: number;
  new_status: string;
  changer_user_id?: number;
}


export interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  subscription: CoachSubscription | null;
  token: string | null;
  isLoading: boolean;
  unreadCount: number;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  registerNewMessageCallback: (callback: (payload: Message) => void) => void;
  unregisterNewMessageCallback: () => void;
  registerMessageDeletedCallback: (callback: (payload: MessageDeletedEventPayload) => void) => void;
  unregisterMessageDeletedCallback: () => void;
  registerMessageReadCallback: (callback: (payload: MessageReadEventPayload) => void) => void;
  unregisterMessageReadCallback: () => void;
  registerAppointmentStatusChangedCallback: (callback: (payload: AppointmentStatusChangedEventPayload) => void) => void;
  unregisterAppointmentStatusChangedCallback: () => void;
  registerShowcaseProgressCallback: (callback: (payload: ShowcaseProgressEventData) => void) => void;
  unregisterShowcaseProgressCallback: () => void;
  registerIntakeFormCallback: (callback: (payload: IntakeFormEventPayload) => void) => void;
  unregisterIntakeFormCallback: () => void;
  registerInvoiceStatusUpdatedCallback: (callback: (payload: InvoiceStatusUpdatedEventPayload) => void) => void;
  unregisterInvoiceStatusUpdatedCallback: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<CoachSubscription | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const eventSourceRef = useRef<EventSource | null>(null);
  const messageCallbackRef = useRef<((payload: Message) => void) | null>(null);
  const messageDeletedCallbackRef = useRef<((payload: MessageDeletedEventPayload) => void) | null>(null);
  const messageReadCallbackRef = useRef<((payload: MessageReadEventPayload) => void) | null>(null);
  const appointmentStatusChangedCallbackRef = useRef<((payload: AppointmentStatusChangedEventPayload) => void) | null>(null);
  const showcaseProgressCallbackRef = useRef<((payload: ShowcaseProgressEventData) => void) | null>(null);
  const intakeFormCallbackRef = useRef<((payload: IntakeFormEventPayload) => void) | null>(null);
  const invoiceStatusUpdatedCallbackRef = useRef<((payload: InvoiceStatusUpdatedEventPayload) => void) | null>(null);

  const registerNewMessageCallback = useCallback((callback: (payload: Message) => void) => {
    messageCallbackRef.current = callback;
  }, []);

  const unregisterNewMessageCallback = useCallback(() => {
    messageCallbackRef.current = null;
  }, []);

  const registerMessageDeletedCallback = useCallback((callback: (payload: MessageDeletedEventPayload) => void) => {
    messageDeletedCallbackRef.current = callback;
  }, []);

  const unregisterMessageDeletedCallback = useCallback(() => {
    messageDeletedCallbackRef.current = null;
  }, []);

  const registerMessageReadCallback = useCallback((callback: (payload: MessageReadEventPayload) => void) => {
    messageReadCallbackRef.current = callback;
  }, []);

  const unregisterMessageReadCallback = useCallback(() => {
    messageReadCallbackRef.current = null;
  }, []);

  const registerAppointmentStatusChangedCallback = useCallback((callback: (payload: AppointmentStatusChangedEventPayload) => void) => {
    appointmentStatusChangedCallbackRef.current = callback;
  }, []);

  const unregisterAppointmentStatusChangedCallback = useCallback(() => {
    appointmentStatusChangedCallbackRef.current = null;
  }, []);

  const registerShowcaseProgressCallback = useCallback((callback: (payload: ShowcaseProgressEventData) => void) => {
    showcaseProgressCallbackRef.current = callback;
  }, []);

  const unregisterShowcaseProgressCallback = useCallback(() => {
    showcaseProgressCallbackRef.current = null;
  }, []);

  const registerIntakeFormCallback = useCallback((callback: (payload: IntakeFormEventPayload) => void) => {
    intakeFormCallbackRef.current = callback;
  }, []);
  const unregisterIntakeFormCallback = useCallback(() => {
    intakeFormCallbackRef.current = null;
  }, []);

  const registerInvoiceStatusUpdatedCallback = useCallback((callback: (payload: InvoiceStatusUpdatedEventPayload) => void) => {
    invoiceStatusUpdatedCallbackRef.current = callback;
  }, []);
  const unregisterInvoiceStatusUpdatedCallback = useCallback(() => {
    invoiceStatusUpdatedCallbackRef.current = null;
  }, []);


  const refreshUnreadCount = useCallback(async () => {
    if (token) {
      try {
        const data = await communicationService.getUnreadNotificationCount();
        setUnreadCount(data.unread_count);
      } catch (error) {
        console.error("Failed to fetch notification count:", error);
      }
    } else {
      setUnreadCount(0);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      const sseUrl = `${API_BASE_URL}/communication/notifications/subscribe?token=${token}`;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      const newEventSource = new EventSource(sseUrl);
      eventSourceRef.current = newEventSource;
      newEventSource.onopen = () => {
        refreshUnreadCount();
      };
      newEventSource.onmessage = (event) => {
        try {
          const eventData = JSON.parse(event.data);

          // --- REAL-TIME EVENT HANDLER ---
          // This block handles specific real-time events with full data payloads.
          if (eventData.event_type === 'new_message' && eventData.data) {
              const newMessage: Message = eventData.data;
              if (messageCallbackRef.current) {
                  // If a callback is registered (i.e., user is on MessagesPage),
                  // call it to handle the real-time update directly.
                  messageCallbackRef.current(newMessage);
              } else {
                  // If no callback, the user is elsewhere. Show a toast.
                  // Avoid showing a toast for your own messages.
                  if (newMessage.sender_user_id !== user?.id) {
                      toast(`New message from ${newMessage.sender?.first_name || 'someone'}`, { icon: '💬' });
                  }
              }
              // Always refresh the unread count for the bell icon.
              refreshUnreadCount();
              return; // Prevent fall-through to generic notification handling
          }

          // --- REAL-TIME COMMENT HANDLER ---
          if (eventData.event_type === 'comment_added' && eventData.data?.comment_data) {
            const newComment: Comment = eventData.data.comment_data;
            
            // Emit a global event that any interested component can listen for.
            eventEmitter.emit('newComment', newComment);
            
            // Show a generic toast and update the unread count.
            if (newComment.user_id !== user?.id) {
              toast(eventData.content, { icon: '💬' });
            }
            refreshUnreadCount();
            return; // Prevent fall-through
          }

          // --- GENERIC NOTIFICATION HANDLER ---
          // This block handles generic notifications from the backend for various events.
          if (eventData.type) { 
              // This is a generic notification, not a specific real-time event.
              if (eventData.event_type === 'message_deleted' && eventData.data?.message_id) {
                if (messageDeletedCallbackRef.current) {
                  messageDeletedCallbackRef.current({ message_id: eventData.data.message_id });
                }
              } else if (eventData.event_type === 'message_read' && eventData.data?.message_id && eventData.data?.read_at) {
                if (messageReadCallbackRef.current) {
                  messageReadCallbackRef.current({
                    message_id: eventData.data.message_id,
                    read_at: eventData.data.read_at,
                    reader_user_id: eventData.data.reader_user_id
                  });
                }
              } else if (eventData.type === NotificationTypeEnum.AppointmentStatusUpdated && eventData.data?.appointment_id && eventData.data?.new_status) {
                if (appointmentStatusChangedCallbackRef.current) {
                  appointmentStatusChangedCallbackRef.current({
                    appointment_id: eventData.data.appointment_id,
                    new_status: eventData.data.new_status,
                    changer_user_id: eventData.data.changer_user_id
                  });
                }
                refreshUnreadCount();
              } else if (eventData.event_type === 'showcase_client_progress' && eventData.data) {
                if (showcaseProgressCallbackRef.current) {
                  showcaseProgressCallbackRef.current(eventData.data);
                }
              } else if (eventData.type?.startsWith('intake_form_') && eventData.data) {
                if (intakeFormCallbackRef.current) {
                    intakeFormCallbackRef.current(eventData.data as IntakeFormEventPayload);
                }
                toast(eventData.content || 'New onboarding update', { icon: '📝' });
                refreshUnreadCount();
              } else if (eventData.type === NotificationTypeEnum.InvoiceStatusUpdated && eventData.data?.invoice_id && eventData.data?.new_status) {
                if (invoiceStatusUpdatedCallbackRef.current) {
                    invoiceStatusUpdatedCallbackRef.current(eventData.data as InvoiceStatusUpdatedEventPayload);
                }
                toast(eventData.content || 'Invoice status updated', { icon: '💰' });
                refreshUnreadCount();
              } else if (eventData.type === NotificationTypeEnum.ResourceSharedWithGroup) {
                toast(eventData.content || 'A new resource was shared with your group.', { icon: '📁' });
                refreshUnreadCount();
              } else if (eventData.content && eventData.type !== NotificationTypeEnum.NewMessage) {
                // Show toast for any other notification that is not a new message,
                // as new messages are handled by the specific real-time handler above.
                toast(eventData.content, { icon: '🔔' });
                refreshUnreadCount();
              }
          }

        } catch (error) {
          console.error("[AuthContext - onmessage] Failed to parse SSE data:", error);
        }
      };
      newEventSource.onerror = (error) => {
        console.error("EventSource failed:", error);
        newEventSource.close();
        refreshUnreadCount();
      };
    } else {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [token, refreshUnreadCount, user]);

  const loadUserAndSetLoading = useCallback(async (currentToken: string | null) => {
    if (!currentToken) {
      setUser(null);
      setSubscription(null);
      setToken(null);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);

    try {
      const currentUser = await authService.getCurrentUser();
      let userWithDetails: User = { ...currentUser };
      
      setToken(currentToken);
      
      if (currentUser.role_name === UserRoleEnum.Coach) {
        const [subData, balanceData] = await Promise.all([
            paymentService.getMySubscription().catch(() => null),
            aiCreditsService.getBalance().catch(() => null)
        ]);
        setSubscription(subData);
        if (balanceData) {
            userWithDetails.ai_credit_balance = balanceData.ai_credit_balance;
        }
      } else {
        setSubscription(null);
      }
      
      setUser(userWithDetails);

    } catch (error) {
      setUser(null);
      setSubscription(null);
      setToken(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      console.error('Failed to load user in AuthContext:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  const refreshUserData = useCallback(async () => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (!currentToken) {
        return;
    }
    try {
        const currentUser = await authService.getCurrentUser();
        let userWithDetails: User = { ...currentUser };

        if (currentUser.role_name === UserRoleEnum.Coach) {
            const [subData, balanceData] = await Promise.all([
                paymentService.getMySubscription().catch(() => null),
                aiCreditsService.getBalance().catch(() => null)
            ]);
            setSubscription(subData);
            if (balanceData) {
                userWithDetails.ai_credit_balance = balanceData.ai_credit_balance;
            }
        } else {
            setSubscription(null);
        }
        setUser(userWithDetails);

    } catch (error) {
        console.error('Silent refresh failed:', error);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setSubscription(null);
    setToken(null);
    setUnreadCount(0);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    loadUserAndSetLoading(storedToken);
    const handleAuthErrorLogout = () => {
      logout();
    };
    window.addEventListener('auth-error-logout', handleAuthErrorLogout);
    return () => window.removeEventListener('auth-error-logout', handleAuthErrorLogout);
  }, [loadUserAndSetLoading, logout]);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    try {
      const data = await authService.login(credentials);
      await loadUserAndSetLoading(data.access_token);
    } catch (error) {
      setUser(null);
      setSubscription(null);
      setToken(null);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setIsLoading(false);
      throw error;
    }
  }, [loadUserAndSetLoading]);

  const loadUser = useCallback(async () => {
    await loadUserAndSetLoading(localStorage.getItem(TOKEN_KEY));
  }, [loadUserAndSetLoading]);

  const contextValue = useMemo(() => ({
    isAuthenticated: !!token && !!user,
    user,
    subscription,
    token,
    isLoading,
    unreadCount,
    login,
    logout,
    loadUser,
    refreshUserData,
    refreshUnreadCount,
    registerNewMessageCallback,
    unregisterNewMessageCallback,
    registerMessageDeletedCallback,
    unregisterMessageDeletedCallback,
    registerMessageReadCallback,
    unregisterMessageReadCallback,
    registerAppointmentStatusChangedCallback,
    unregisterAppointmentStatusChangedCallback,
    registerShowcaseProgressCallback,
    unregisterShowcaseProgressCallback,
    registerIntakeFormCallback,
    unregisterIntakeFormCallback,
    registerInvoiceStatusUpdatedCallback,
    unregisterInvoiceStatusUpdatedCallback,
  }), [
    token, user, subscription, isLoading, unreadCount, login, logout, loadUser,
    refreshUserData,
    refreshUnreadCount, registerNewMessageCallback, unregisterNewMessageCallback,
    registerMessageDeletedCallback, unregisterMessageDeletedCallback,
    registerMessageReadCallback, unregisterMessageReadCallback,
    registerAppointmentStatusChangedCallback, unregisterAppointmentStatusChangedCallback,
    registerShowcaseProgressCallback, unregisterShowcaseProgressCallback,
    registerIntakeFormCallback, unregisterIntakeFormCallback,
    registerInvoiceStatusUpdatedCallback, unregisterInvoiceStatusUpdatedCallback
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};